<template>
  <BaseModalEditCreate
    v-if="isOpen"
    @dismissChanges="onDismissChanges"
    @save="onSave"
    :disable="mode != 'edit'"
    maxWidth="extra"
    :isModified="shouldWarnBeforeClose"
    :title="isNew ? $t('common.actions.create') : modifiedData?.alterCode.name">
    <template #form>
      <div v-if="materialAlterCode && modifiedData" class="w-full h-full overflow-y-auto">
        <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
          <tbody>
            <BaseDetailsTableRow :name="$t('material.name')" :mode="mode">
              <BaseInput
                v-model="modifiedData.alterCode.name"
                color="base"
                :mode="mode"
                flat
                show-error-as-tooltip
                :error="$v.name.required.$invalid ? $v.name.required.$message : ''"
                class="w-full" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.code')" :mode="mode">
              <BaseInput
                v-model="modifiedData.alterCode.code"
                color="base"
                :mode="mode"
                flat
                show-error-as-tooltip
                :error="$v.code.required.$invalid ? $v.code.required.$message : ''" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.alterCodeType')" :mode="mode">
              <BaseSelectDropdown
                v-if="dropDownAlterCodeTypes && dropDownAlterCodeTypes.length > 0"
                :items="dropDownAlterCodeTypes"
                :selected-item="selectedAlterCodetype"
                name="alterCodeType"
                flat
                size="sm"
                mode="view" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.smallCode')" :mode="mode">
              <BaseInput v-model="modifiedData.shortCode" color="base" :mode="mode" flat />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.measurementUnit')" :mode="mode">
              <BaseSelectDropdown
                v-if="dropdownMeasurementUnits && dropdownMeasurementUnits.length > 0"
                :items="dropdownMeasurementUnits"
                :selected-item="selectedMeasurementUnit"
                name="measurementUnit"
                flat
                size="sm"
                :mode="mode"
                clearable
                show-error-as-tooltip
                :error="
                  $v.measurementUnitId.required.$invalid
                    ? $v.measurementUnitId.required.$message
                    : ''
                "
                @update:selected="handleMeasurementSelection" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.relationFactor')" :mode="mode">
              <BaseInput
                v-model="modifiedData.relationFactor"
                color="base"
                :mode="mode"
                flat
                type="number"
                show-error-as-tooltip
                :error="
                  $v.relationFactor.minValue.$invalid ? $v.relationFactor.minValue.$message : ''
                " />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.minSalesQuantity')" :mode="mode">
              <BaseInput
                v-model="modifiedData.minSalesQuantity"
                color="base"
                :mode="mode"
                flat
                type="number" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.lotStart')" :mode="mode">
              <BaseInput v-model="modifiedData.lotStart" color="base" :mode="mode" flat />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.lotFinish')" :mode="mode">
              <BaseInput v-model="modifiedData.lotFinsh" color="base" :mode="mode" flat />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.dimension', { 'number': 1 })" :mode="mode">
              <BaseInput
                v-model="modifiedData.dimension1"
                color="base"
                :mode="mode"
                flat
                type="number" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.dimension', { 'number': 2 })" :mode="mode">
              <BaseInput
                v-model="modifiedData.dimension2"
                color="base"
                :mode="mode"
                flat
                type="number" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.dimension', { 'number': 3 })" :mode="mode">
              <BaseInput
                v-model="modifiedData.dimension3"
                color="base"
                :mode="mode"
                flat
                type="number" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('baseObject.sequence')" :mode="mode">
              <BaseInput
                v-model="modifiedData.sequence"
                color="base"
                :mode="mode"
                flat
                type="number"
                show-error-as-tooltip
                :error="
                  $v.sequence.minValue.$invalid
                    ? $v.sequence.minValue.$message
                    : $v.sequence.uniqueSequence.$invalid
                      ? $v.sequence.uniqueSequence.$message
                      : ''
                " />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.default')" :mode="mode">
              <BaseTableCellStatus
                v-if="mode == 'view'"
                :item="modifiedData.isDefault ? modifiedData.isDefault : 0" />
              <BaseToggle v-else label="Default" name="default" v-model="modifiedData.isDefault" />
            </BaseDetailsTableRow>

            <BaseDetailsTableRow :name="$t('material.quantityTotalizer')" :mode="mode">
              <BaseTableCellStatus
                v-if="mode == 'view'"
                :item="modifiedData.quantityTotalizer ? modifiedData.quantityTotalizer : 0" />
              <BaseToggle
                v-else
                label="Default"
                name="quantityTotalizer"
                v-model="modifiedData.quantityTotalizer" />
            </BaseDetailsTableRow>
          </tbody>
        </table>
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { helpers, required, minValue } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { deepCopy } from '@/utils/data';
import BaseInput from '../common/BaseInput.vue';
import BaseToggle from '../common/BaseToggle.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';

import type { MaterialAlterCode } from '@/types/Materials';
import type { SelectDropdownItem } from '@/types/Base';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  materialAlterCode: MaterialAlterCode;
  isOpen: boolean;
  isNew: boolean;
  mode: 'view' | 'edit';
  sequenceList?: number[];
}>();
const emit = defineEmits(['close', 'save']);

const modifiedData = ref<MaterialAlterCode>(deepCopy<MaterialAlterCode>(props.materialAlterCode));
const selectedMeasurementUnit = ref<SelectDropdownItem | null>(null);
const selectedAlterCodetype = ref<SelectDropdownItem | null>(null);

const { dropdownMeasurementUnits, dropDownAlterCodeTypes } = storeToRefs(useLookupsStore());

function initDropdowns() {
  if (modifiedData.value) {
    const measurementValue = dropdownMeasurementUnits.value.find(
      (item) => item.value == modifiedData.value?.measurementUnitId
    );
    if (measurementValue) selectedMeasurementUnit.value = measurementValue;
    else selectedMeasurementUnit.value = null;

    const alterCodeTypeValue = dropDownAlterCodeTypes.value.find(
      (item) => item.value == modifiedData.value?.alterCode.alterCodeTypeId
    );
    if (alterCodeTypeValue) selectedAlterCodetype.value = alterCodeTypeValue;
    else selectedAlterCodetype.value = null;

    if (!modifiedData.value.sequence && props.mode === 'edit') {
      if (props.sequenceList && props.sequenceList.length > 0)
        modifiedData.value.sequence = Math.max(...props.sequenceList) + 1;
      else modifiedData.value.sequence = 1;
    }
  }
}

function handleMeasurementSelection(item: SelectDropdownItem) {
  if (!modifiedData.value) return;
  selectedMeasurementUnit.value = item ? item : null;
  modifiedData.value.measurementUnitId = item ? (item.value as string) : '';
  modifiedData.value.measurementUnit.name = item ? (item.name as string) : '';
  validationModel.value.measurementUnitId = item ? (item.value as string) : '';
}

const shouldWarnBeforeClose = ref(false);

function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;

  shouldWarnBeforeClose.value = false;
  emit('save', modifiedData.value);
  $v.value.$reset();
}

const validationModel = ref({
  name: modifiedData.value.alterCode.name || '',
  code: modifiedData.value.alterCode.code || '',
  measurementUnitId: modifiedData.value.measurementUnitId || null,
  relationFactor: modifiedData.value.relationFactor || null,
  sequence: modifiedData.value.sequence || null,
});

const uniqueSequence = (value: number | null) => {
  if (value === null) return true;
  if (!props.sequenceList) return true;
  const originalSequence = props.materialAlterCode.sequence;
  const otherSequences = props.sequenceList.filter((seq) => seq !== originalSequence);
  return !otherSequences.includes(value);
};

const { t: $t } = useI18n({ useScope: 'global' });
const rules = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  code: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  measurementUnitId: {
    required: helpers.withMessage($t('messages.errors.requiredField'), required),
  },
  relationFactor: {
    minValue: helpers.withMessage($t('messages.errors.greaterThan0'), minValue(0.01)),
  },
  sequence: {
    minValue: helpers.withMessage($t('messages.errors.greaterThan0'), minValue(1)),
    uniqueSequence: helpers.withMessage($t('messages.errors.sequenceMustBeUnique'), uniqueSequence),
  },
}));
const $v = useVuelidate(rules, validationModel, { $lazy: true, $scope: false });

function onDismissChanges() {
  modifiedData.value = deepCopy<MaterialAlterCode>(props.materialAlterCode);
  initDropdowns();
  $v.value.$reset();
  emit('close');
}

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.alterCode.name || '';
    validationModel.value.code = newData.alterCode.code || '';
    validationModel.value.measurementUnitId = newData.measurementUnitId || '';
    validationModel.value.relationFactor = newData.relationFactor || null;
    validationModel.value.sequence = newData.sequence ?? null;

    shouldWarnBeforeClose.value =
      JSON.stringify(modifiedData.value) !== JSON.stringify(props.materialAlterCode);
  },
  { deep: true }
);

watch(
  () => props.materialAlterCode,
  (newData) => {
    modifiedData.value = deepCopy<MaterialAlterCode>(newData);
    initDropdowns();
  }
);

onMounted(async () => {
  modifiedData.value = deepCopy<MaterialAlterCode>(props.materialAlterCode);
  initDropdowns();
});
</script>
